<template>
  <section class="seeusers__policy">
    <div class="seeusers__policy__header">
      <p class="seeusers__policy__title">Acceso al módulo de Usuarios</p>
      <ToggleInput :value="toggleEnabled" @input="handleToggleChange" label="" />
    </div>
    <p class="seeusers__policy__desc">Controla si el usuario puede ingresar al módulo. Al activarlo se habilita la configuración de visibilidad por sede.</p>

    <div class="seeusers__policy__toolbar">
      <div class="seeusers__policy__search">
        <i class="fas fa-search seeusers__policy__search-icon"></i>
        <input type="search" v-model="searchTerm" placeholder="Busca por sede o ciudad" class="seeusers__policy__search-input" />
      </div>

      <div class="seeusers__policy__summary">
        <span class="seeusers__policy__summary-count"
          ><strong>{{ checkedCount }}</strong> de {{ totalCount }} sedes habilitadas</span
        >
        <CustomCheckbox id="select-all-sedes" label="Seleccionar todas" :isChecked="allFilteredSelected" @toggleSelection="toggleSelectAll" />
      </div>
    </div>

    <div class="seeusers__policy__columns">
      <span>Sede · Ciudad</span>
      <span>Tipo de usuarios visibles</span>
    </div>

    <ul class="seeusers__policy__list">
      <li class="seeusers__policy__row" :class="{ 'seeusers__policy__row--checked': sede.isChecked }" v-for="sede in filteredSedes" :key="`${sede.cityName}-${sede.officeName}`">
        <label class="seeusers__policy__checkbox">
          <input type="checkbox" :checked="sede.isChecked" @change="toggleSedeCheck(sede, $event)" />
          <span class="seeusers__policy__checkbox-mark" />
        </label>

        <div class="seeusers__policy__info">
          <strong class="seeusers__policy__info-name">{{ sede.officeName || sede.cityName }}</strong>
          <span class="seeusers__policy__info-city" v-if="sede.officeName">{{ sede.cityName }}</span>
        </div>

        <div class="seeusers__policy__types" v-if="sede.officeName">
          <span class="seeusers__policy__type-thumb" :style="{ transform: `translateX(${typeIndex(sede) * 100}%)` }" />
          <button
            type="button"
            class="seeusers__policy__type-btn"
            :class="{ 'seeusers__policy__type-btn--active': sede.userType === type.key }"
            v-for="type in userTypes"
            :key="type.key"
            @click="selectType(sede, type.key)">
            {{ type.label }}
          </button>
        </div>
      </li>
    </ul>

    <p class="seeusers__policy__hint">Marca una sede para habilitar su visibilidad y elige qué tipo de usuarios podrá ver dentro de ella.</p>
  </section>
</template>

<script>
  import CustomCheckbox from "@/components/input/CustomCheckbox.vue";
  import ToggleInput from "@/components/atoms/ToggleInput.vue";

  export default {
    props: ["policy", "user"],
    components: { CustomCheckbox, ToggleInput },
    data() {
      return {
        sedes: [],
        searchTerm: "",
        previous: this.user.polices?.["see-models"]?.payload,
        hasAddedCity: false,
        toggleEnabled: !!this.user.polices?.["see-models"]?.status,
        userTypes: [
          { key: "models", label: "Models" },
          { key: "roa", label: "ROA" },
          { key: "both", label: "Ambos" },
        ],
      };
    },
    computed: {
      citiesDb() {
        const cities = this.$store.state.cities.data || [];
        const hasInactives = cities.some((city) => city?.cityName?.toLowerCase() === "inactivos");

        if (hasInactives) {
          return [...cities];
        }

        return [...cities, { cityName: "Inactivos", isChecked: false, offices: [] }];
      },
      offices() {
        return this.$store.getters["offices/getData"];
      },
      realSedes() {
        return this.sedes.filter((sede) => sede.officeName !== null);
      },
      checkedCount() {
        return this.realSedes.filter((sede) => sede.isChecked).length;
      },
      totalCount() {
        return this.realSedes.length;
      },
      filteredSedes() {
        const term = this.searchTerm.trim().toLowerCase();
        if (!term) {
          return this.sedes;
        }
        return this.sedes.filter((sede) => {
          const name = (sede.officeName || sede.cityName).toLowerCase();
          return name.includes(term) || sede.cityName.toLowerCase().includes(term);
        });
      },
      filteredRealSedes() {
        return this.filteredSedes.filter((sede) => sede.officeName !== null);
      },
      allFilteredSelected() {
        return this.filteredRealSedes.length > 0 && this.filteredRealSedes.every((sede) => sede.isChecked);
      },
      buildResult() {
        const cityCoordinator = this.user.role === "coordinator" ? this.user.city : null;
        const hasPolice = !!this.previous;
        const allOptionsSeleted = {};

        const officesByCity = this.realSedes.reduce((result, sede) => {
          result[sede.cityName] = result[sede.cityName] || [];
          result[sede.cityName].push(sede);
          return result;
        }, {});

        Object.entries(officesByCity).forEach(([cityName, sedesOfCity]) => {
          const checkedSedes = sedesOfCity.filter((sede) => sede.isChecked);
          if (!checkedSedes.length) {
            return;
          }
          allOptionsSeleted[cityName] = checkedSedes.reduce((types, sede) => {
            types[sede.officeName] = sede.userType || "both";
            return types;
          }, {});
          if (cityName === cityCoordinator) {
            this.hasAddedCity = true;
          }
        });

        this.sedes
          .filter((sede) => sede.officeName === null && sede.isChecked)
          .forEach((sede) => {
            allOptionsSeleted[sede.cityName] = {};
            if (sede.cityName === cityCoordinator) {
              this.hasAddedCity = true;
            }
          });

        if (cityCoordinator && !hasPolice && !allOptionsSeleted.hasOwnProperty(cityCoordinator) && !this.hasAddedCity && Object.values(allOptionsSeleted).length) {
          const sedesOfCity = officesByCity[cityCoordinator] || [];
          allOptionsSeleted[cityCoordinator] = sedesOfCity.reduce((types, sede) => {
            types[sede.officeName] = sede.userType || "both";
            return types;
          }, {});
          this.hasAddedCity = true;
        }

        return { allOptionsSeleted };
      },
    },
    methods: {
      handleToggleChange(checked) {
        this.toggleEnabled = checked;
        this.$emit("toggle-status", checked);
      },
      activateToggle() {
        if (this.toggleEnabled) {
          return;
        }
        this.toggleEnabled = true;
        this.$emit("toggle-status", true);
      },
      createSedes() {
        const polices = this.user.polices?.["see-models"]?.payload ?? {};
        const sedes = [];

        this.citiesDb.forEach((city) => {
          const officeFilter = this.offices.filter((office) => office.cityName.toUpperCase() === city.cityName.toUpperCase());

          if (!officeFilter.length) {
            sedes.push({
              cityName: city.cityName,
              officeName: null,
              isChecked: Object.prototype.hasOwnProperty.call(polices, city.cityName),
              userType: null,
            });
            return;
          }

          const cityValue = polices[city.cityName];

          officeFilter.forEach((office) => {
            let isChecked = false;
            let userType = "both";

            if (Array.isArray(cityValue)) {
              isChecked = cityValue.includes(office.officeName);
            } else if (cityValue && typeof cityValue === "object") {
              isChecked = Object.prototype.hasOwnProperty.call(cityValue, office.officeName);
              userType = cityValue[office.officeName] || "both";
            }

            sedes.push({
              cityName: office.cityName,
              officeName: office.officeName,
              isChecked,
              userType,
            });
          });
        });

        return sedes;
      },

      toggleSedeCheck(sede, event) {
        sede.isChecked = event.target.checked;
        if (sede.isChecked && !sede.userType) {
          sede.userType = "both";
        }
        if (sede.isChecked) {
          this.activateToggle();
        }
        this.$emit("result-updated", { politic: this.buildResult });
      },

      typeIndex(sede) {
        return this.userTypes.findIndex((type) => type.key === sede.userType);
      },

      selectType(sede, type) {
        sede.userType = type;
        sede.isChecked = true;
        this.activateToggle();
        this.$emit("result-updated", { politic: this.buildResult });
      },

      toggleSelectAll() {
        const shouldCheck = !this.allFilteredSelected;
        this.filteredRealSedes.forEach((sede) => {
          sede.isChecked = shouldCheck;
          if (shouldCheck && !sede.userType) {
            sede.userType = "both";
          }
        });
        if (shouldCheck) {
          this.activateToggle();
        }
        this.$emit("result-updated", { politic: this.buildResult });
      },
      getResult() {
        const { allOptionsSeleted = {} } = this.buildResult;

        return !!Object.keys(allOptionsSeleted).length;
      },
    },

    mounted() {
      this.sedes = this.createSedes();
    },
  };
</script>

<style lang="scss">
  .seeusers__policy {
    width: 100%;

    &__header {
      @include Flex(row, space-between, center);
    }

    &__title {
      font-size: 16px;
      font-family: $newFont_medium;
      font-weight: 500;
      color: #3d3d3d;
    }

    &__desc {
      color: #595959;
      font-size: 14px;
      font-family: $newFont_regular;
      margin: 6px 0 14px;
    }

    &__toolbar {
      @include Flex(row, space-between, center);
      flex-wrap: wrap;
      gap: 10px;
      margin-bottom: 12px;
    }

    &__search {
      position: relative;
      flex: 1 1 220px;
      max-width: 320px;

      &-icon {
        position: absolute;
        top: 50%;
        left: 12px;
        transform: translateY(-50%);
        color: $alto-notify;
        font-size: 13px;
      }

      &-input {
        width: 80%;
        border: 1px solid $alto;
        border-radius: 8px;
        padding: 8px 10px 8px 32px;
        font-size: 14px;
        font-family: $newFont_regular;
        transition: border-color 0.2s ease;

        &:hover,
        &:focus {
          border-color: #7c7c7c;
          outline: none;
        }
      }
    }

    &__summary {
      @include Flex(row, flex-end, center);
      gap: 20px;

      &-count {
        font-size: 14px;
        font-family: $newFont_regular;
        strong {
          color: $primary-color;
        }
      }
    }

    &__columns {
      @include Flex(row, space-between, center);
      background: #f7f7f8;
      border-radius: 6px 6px 0 0;
      padding: 10px 14px;
      font-size: 12px;
      border: 1px solid #e5e5e5;
      font-family: $newFont_medium;
      letter-spacing: 0.03em;
      text-transform: uppercase;
      border-radius: 12px 12px 0px 0px;

      color: #3d3d3d;
    }

    &__list {
      max-height: 320px;
      overflow-y: auto;
      border: 1px solid #e5e5e5;
      border-top: none;
      list-style: none;
      border-radius: 0px 0px 12px 12px;

      &::-webkit-scrollbar {
        width: 2px;
      }
    }

    &__row {
      @include Flex(row, space-between, center);
      gap: 10px;
      padding: 10px 14px;
      border-top: 1px solid $alto;
      border-left: 3px solid transparent;
      opacity: 0.55;
      transition: opacity 0.2s ease, background-color 0.2s ease;

      &:first-child {
        border-top: none;
      }

      &:hover {
        background-color: #f7f7f8;
        opacity: 1;
      }

      &--checked {
        border-left-color: $primary-color;
        opacity: 1;
      }
    }

    &__checkbox {
      position: relative;
      @include Flex(row);
      cursor: pointer;
      flex-shrink: 0;

      input {
        position: absolute;
        opacity: 0;
        width: 20px;
        height: 20px;
        cursor: pointer;
      }

      &-mark {
        width: 20px;
        height: 20px;
        border-radius: 4px;
        border: 1px solid #bfbdbd;
        background-color: #fff;
        position: relative;
        transition: background-color 0.2s ease;

        &::after {
          content: "";
          position: absolute;
          display: none;
          top: -3px;
          bottom: 0;
          left: 0;
          right: 0;
          margin: auto;
          width: 5px;
          height: 9px;
          border: solid #fff;
          border-width: 0 2px 2px 0;
          transform: rotate(30deg);
        }
      }

      input:checked + &-mark {
        background-color: $primary-color;
        border-color: $primary-color;

        &::after {
          display: block;
        }
      }
    }

    &__info {
      @include Flex(column, center, flex-start);
      flex: 1;

      &-name {
        font-size: 14px;
        font-family: $newFont_medium;
        color: #3d3d3d;
      }

      &-city {
        font-size: 12px;
        font-family: $newFont_regular;
        color: #595959;
      }
    }

    &__types {
      position: relative;
      @include Flex(row, flex-end, center);
      flex-shrink: 0;
      background: #f0f0f1;
      border-radius: 50px;
      padding: 3px;
    }

    &__type-thumb {
      position: absolute;
      top: 3px;
      bottom: 3px;
      left: 3px;
      width: calc((100% - 6px) / 3);
      background: #fff;
      border-radius: 50px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
      transition: transform 0.25s ease;
      z-index: 0;
    }

    &__type-btn {
      position: relative;
      z-index: 1;
      flex: 1;
      border: none;
      background: transparent;
      color: #3d3d3d;
      border-radius: 50px;
      padding: 6px 16px;
      font-size: 13px;
      font-family: $newFont_medium;
      cursor: pointer;
      transition: color 0.15s ease;

      &--active {
        color: $primary-color;
      }
    }

    &__hint {
      font-size: 12px;
      color: #595959;
      margin-top: 10px;
    }
  }
</style>
